import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Video,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  RefreshCw,
  User,
  Calendar,
  Eye,
  Send,
  ExternalLink,
  X
} from "lucide-react";
import {
  getAllVideoCloneRequests,
  updateVideoCloneRequest,
  getVideoCloneStatistics,
  type VideoCloneRequestWithUser
} from "@/lib/video-clone-service";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const CloneVDOManagement = () => {
  const { toast } = useToast();
  const [requests, setRequests] = useState<VideoCloneRequestWithUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<VideoCloneRequestWithUser | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [filter, setFilter] = useState<'all' | 'pending' | 'processing' | 'completed' | 'rejected'>('all');

  // Response form state
  const [adminResponse, setAdminResponse] = useState("");
  const [prompts, setPrompts] = useState<string[]>(["", "", ""]);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const data = await getAllVideoCloneRequests();
      setRequests(data);
    } catch (error) {
      console.error("Error fetching video clone requests:", error);
      toast({
        title: "Error",
        description: "Failed to load requests",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">เสร็จแล้ว</Badge>;
      case "pending":
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">รอตรวจสอบ</Badge>;
      case "processing":
        return <Badge className="bg-blue-500/20 text-blue-500 border-blue-500/30">กำลังดำเนินการ</Badge>;
      case "rejected":
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">ถูกปฏิเสธ</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleOpenRequest = (request: VideoCloneRequestWithUser) => {
    setSelectedRequest(request);
    setAdminResponse(request.admin_response || "");
    setPrompts(request.result_prompts || ["", "", ""]);
  };

  const handleCloseDialog = () => {
    setSelectedRequest(null);
    setAdminResponse("");
    setPrompts(["", "", ""]);
  };

  const handlePromptChange = (index: number, value: string) => {
    const newPrompts = [...prompts];
    newPrompts[index] = value;
    setPrompts(newPrompts);
  };

  const handleAddPrompt = () => {
    setPrompts([...prompts, ""]);
  };

  const handleRemovePrompt = (index: number) => {
    setPrompts(prompts.filter((_, i) => i !== index));
  };

  const handleSubmitResponse = async (status: 'completed' | 'rejected') => {
    if (!selectedRequest) return;

    setIsUpdating(true);
    try {
      const validPrompts = status === 'completed'
        ? prompts.filter(p => p.trim().length > 0)
        : null;

      const success = await updateVideoCloneRequest(
        selectedRequest.id,
        status,
        validPrompts,
        null,
        adminResponse.trim() || null
      );

      if (success) {
        toast({
          title: status === 'completed' ? "ตอบกลับสำเร็จ!" : "ปฏิเสธคำขอแล้ว",
          description: status === 'completed'
            ? "User จะได้รับการแจ้งเตือน"
            : "User จะได้รับการแจ้งเตือน",
        });

        // Refresh the list
        await fetchRequests();
        handleCloseDialog();
      } else {
        throw new Error("Failed to update request");
      }
    } catch (error) {
      console.error("Error updating request:", error);
      toast({
        title: "Error",
        description: "Failed to update request",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const filteredRequests = requests.filter(request => {
    if (filter === 'all') return true;
    return request.status === filter;
  });

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    processing: requests.filter(r => r.status === 'processing').length,
    completed: requests.filter(r => r.status === 'completed').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Video className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">ทั้งหมด</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-xs text-muted-foreground">รอตรวจสอบ</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Loader2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.processing}</p>
                <p className="text-xs text-muted-foreground">กำลังดำเนินการ</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.completed}</p>
                <p className="text-xs text-muted-foreground">เสร็จแล้ว</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card variant="glass">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.rejected}</p>
                <p className="text-xs text-muted-foreground">ถูกปฏิเสธ</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests List */}
      <Card variant="glass">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            คำขอ Clone VDO
          </CardTitle>
          <div className="flex items-center gap-2">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
              <TabsList>
                <TabsTrigger value="all">ทั้งหมด</TabsTrigger>
                <TabsTrigger value="pending">รอตรวจสอบ</TabsTrigger>
                <TabsTrigger value="processing">กำลังดำเนินการ</TabsTrigger>
                <TabsTrigger value="completed">เสร็จแล้ว</TabsTrigger>
                <TabsTrigger value="rejected">ถูกปฏิเสธ</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button variant="outline" size="icon" onClick={fetchRequests} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-12">
              <Video className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">ไม่มีคำขอ</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredRequests.map((request) => (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border border-border/50 cursor-pointer"
                  onClick={() => handleOpenRequest(request)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">
                        {request.profiles?.display_name || request.profiles?.email || 'Unknown User'}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span>{request.video_url || request.video_file_name || 'No URL/File'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(request.submitted_at)}</span>
                        <span>•</span>
                        <span>{request.credits_used} credits</span>
                      </div>
                      {request.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                          {request.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getStatusBadge(request.status)}
                    {request.video_url && (
                      <Button
                        variant="outline"
                        size="icon"
                        asChild
                        onClick={(e) => e.stopPropagation()}
                      >
                        <a href={request.video_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Response Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>ตอบกลับคำขอ Clone VDO</DialogTitle>
            <DialogDescription>
              ดูวิดีโอต้นฉบับและเขียน Prompts ตอบกลับให้ User
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <div className="space-y-4">
              {/* User Info */}
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-medium">
                  User: {selectedRequest.profiles?.display_name || selectedRequest.profiles?.email || 'Unknown'}
                </p>
                <p className="text-xs text-muted-foreground">
                  ส่งเมื่อ: {formatDate(selectedRequest.submitted_at)}
                </p>
              </div>

              {/* Video URL */}
              {selectedRequest.video_url && (
                <div>
                  <label className="text-sm font-medium mb-2 block">Video URL:</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={selectedRequest.video_url}
                      className="flex-1 px-3 py-2 text-sm bg-muted rounded-lg"
                    />
                    <Button variant="outline" size="icon" asChild>
                      <a href={selectedRequest.video_url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedRequest.description && (
                <div>
                  <label className="text-sm font-medium mb-2 block">คำอธิบายจาก User:</label>
                  <div className="p-3 rounded-lg bg-muted/50 text-sm">
                    {selectedRequest.description}
                  </div>
                </div>
              )}

              {/* Admin Response Message */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  ข้อความตอบกลับ User (Optional):
                </label>
                <Textarea
                  placeholder="เขียนข้อความอธิบายเพิ่มเติมให้ User..."
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Prompts */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium">Prompts สำหรับสร้างวิดีโอ:</label>
                  <Button variant="outline" size="sm" onClick={handleAddPrompt}>
                    + เพิ่ม Prompt
                  </Button>
                </div>
                <div className="space-y-2">
                  {prompts.map((prompt, index) => (
                    <div key={index} className="flex gap-2">
                      <Textarea
                        placeholder={`Prompt #${index + 1}`}
                        value={prompt}
                        onChange={(e) => handlePromptChange(index, e.target.value)}
                        rows={2}
                        className="flex-1"
                      />
                      {prompts.length > 1 && (
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleRemovePrompt(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  User สามารถคัดลอก Prompts เหล่านี้ไปใช้สร้างวิดีโอได้
                </p>
              </div>

              {/* Existing Admin Notes */}
              {selectedRequest.admin_notes && (
                <div>
                  <label className="text-sm font-medium mb-2 block">โน้ตเดิม:</label>
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">
                    {selectedRequest.admin_notes}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseDialog} disabled={isUpdating}>
              ยกเลิก
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleSubmitResponse('rejected')}
              disabled={isUpdating}
            >
              <XCircle className="h-4 w-4 mr-2" />
              ปฏิเสธคำขอ
            </Button>
            <Button
              onClick={() => handleSubmitResponse('completed')}
              disabled={isUpdating}
            >
              <Send className="h-4 w-4 mr-2" />
              ส่งคำตอบ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CloneVDOManagement;
